// GuestFacePills — the "People at this event" strip in the gallery. Everyone
// who opted in to auto-tagging shows up with their face icon. Tap one or MORE
// people to build a virtual album: with several selected, only photos that
// contain ALL of them are shown (e.g. Shawna + Bill together).
import React from "react";
import { ScanFace, X as XIcon, Users, Check } from "lucide-react";

// Deterministic pastel color per guest name so pills feel personal.
const PILL_COLORS = ["#7F4BFF", "#22E2FF", "#FF7A00", "#FF1F6B", "#22FFB7", "#FFD300", "#4D8FFF", "#E63DDB"];
export function colorForName(name) {
  const s = (name || "").toLowerCase();
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return PILL_COLORS[h % PILL_COLORS.length];
}

export function FaceAvatar({ reg, size = 22, active = false }) {
  const color = colorForName(reg.name);
  if (reg.avatar) {
    return (
      <img
        src={reg.avatar}
        alt=""
        className="rounded-full object-cover shrink-0"
        style={{
          width: size,
          height: size,
          border: `1.5px solid ${active ? color : "rgba(255,255,255,0.25)"}`,
          boxShadow: active ? `0 0 10px ${color}88` : "none",
        }}
        draggable={false}
      />
    );
  }
  return (
    <span
      className="rounded-full flex items-center justify-center shrink-0 font-semibold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.45,
        background: `${color}33`,
        border: `1.5px solid ${active ? color : `${color}66`}`,
        color: "#fff",
      }}
    >
      {(reg.name || "?").charAt(0).toUpperCase()}
    </span>
  );
}

export default function GuestFacePills({
  registrations,
  activeGuestIds,     // array of selected guest_ids (multi-select)
  indexingProgress,   // {done, total} or null
  faceMode,           // "and" (together) | "or" (any)
  onModeChange,
  virtualAlbums,      // creator's saved combos [{id, name, guest_ids, mode}]
  onApplyVirtualAlbum,
  onDeleteVirtualAlbum,
  onToggleGuest,
  onClear,
  onOpenPeople,       // opens the People modal
  onAddFace,          // opens the selfie registration modal
}) {
  const selected = new Set(activeGuestIds || []);
  const hasContent =
    (Array.isArray(registrations) && registrations.length > 0) ||
    (Array.isArray(virtualAlbums) && virtualAlbums.length > 0);

  if (!hasContent) {
    return (
      <div className="mb-4 flex items-center gap-3 flex-wrap" data-testid="face-empty-row">
        <button
          type="button"
          onClick={onAddFace}
          className="inline-flex items-center gap-1.5 text-[12px] font-medium px-3 py-1.5 rounded-full"
          style={{ background: "rgba(127,75,255,0.10)", border: "1px dashed rgba(127,75,255,0.5)", color: "#C9B6FF" }}
          data-testid="add-face-btn"
        >
          <ScanFace size={13} /> Add your face — let friends find you here
        </button>
        {indexingProgress && indexingProgress.total > 0 && indexingProgress.done < indexingProgress.total && (
          <span className="text-xs flex items-center gap-1.5" style={{ color: "rgba(255,255,255,0.55)" }}
                data-testid="face-indexing-status">
            <ScanFace size={12} /> Scanning faces… {indexingProgress.done}/{indexingProgress.total}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4" data-testid="guest-face-pills">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <div className="text-xs font-semibold tracking-wider" style={{ color: "rgba(255,255,255,0.65)" }}>
          PEOPLE AT THIS EVENT
        </div>
        {indexingProgress && indexingProgress.total > 0 && indexingProgress.done < indexingProgress.total && (
          <div className="text-[10px] px-2 py-0.5 rounded-full"
               style={{ background: "rgba(127,75,255,0.14)", color: "#C9B6FF", border: "1px solid rgba(127,75,255,0.35)" }}>
            indexing {indexingProgress.done}/{indexingProgress.total}
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onAddFace}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full"
            style={{ background: "rgba(127,75,255,0.10)", border: "1px dashed rgba(127,75,255,0.5)", color: "#C9B6FF" }}
            data-testid="add-face-btn"
          >
            <ScanFace size={11} /> Add your face
          </button>
          <button
            type="button"
            onClick={onOpenPeople}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full"
            style={{ background: "rgba(34,226,255,0.10)", border: "1px solid rgba(34,226,255,0.35)", color: "#A8F2FF" }}
            data-testid="open-people-modal"
          >
            <Users size={11} /> View all ({registrations.length})
          </button>
        </div>
      </div>

      {/* Saved virtual albums — private to this guest */}
      {Array.isArray(virtualAlbums) && virtualAlbums.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-2" data-testid="virtual-album-chips">
          <span className="text-[10px] font-semibold tracking-wider" style={{ color: "rgba(255,255,255,0.45)" }}>
            MY VIRTUAL ALBUMS
          </span>
          {virtualAlbums.map((va) => (
            <span
              key={va.id}
              className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full text-xs font-medium"
              style={{ background: "rgba(255,122,0,0.10)", border: "1px solid rgba(255,122,0,0.45)", color: "#FFC299" }}
              data-testid={`virtual-album-chip-${va.id}`}
            >
              <button type="button" onClick={() => onApplyVirtualAlbum?.(va)} data-testid={`virtual-album-apply-${va.id}`}>
                {va.name} <span className="opacity-60">({va.mode === "or" ? "any" : "together"})</span>
              </button>
              <button
                type="button"
                onClick={() => onDeleteVirtualAlbum?.(va)}
                className="w-4 h-4 rounded-full flex items-center justify-center opacity-60 hover:opacity-100"
                aria-label={`Delete ${va.name}`}
                data-testid={`virtual-album-delete-${va.id}`}
              >
                <XIcon size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {registrations.map((r) => {
          const active = selected.has(r.guest_id);
          const color = colorForName(r.name);
          return (
            <button
              key={r.guest_id}
              type="button"
              onClick={() => onToggleGuest?.(r)}
              className="inline-flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full text-sm font-medium transition"
              style={{
                background: active ? `${color}33` : "rgba(255,255,255,0.04)",
                border: `1px solid ${active ? color : "rgba(255,255,255,0.12)"}`,
                color: active ? "#fff" : "rgba(255,255,255,0.85)",
                boxShadow: active ? `0 6px 22px -8px ${color}88` : "none",
              }}
              data-testid={`guest-face-pill-${r.guest_id}`}
              title={active ? `Remove ${r.name} from the filter` : `See photos of ${r.name}`}
            >
              <FaceAvatar reg={r} active={active} />
              <span>{r.name}</span>
              {active && <Check size={12} className="opacity-90" />}
            </button>
          );
        })}
        {selected.size > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs"
            style={{ background: "rgba(255,107,107,0.08)", border: "1px solid rgba(255,107,107,0.35)", color: "#FF9B9B" }}
            data-testid="clear-all-face-pills"
          >
            <XIcon size={11} /> Clear
          </button>
        )}
      </div>

      {/* AND / OR mode toggle — meaningful once 2+ people are selected */}
      {selected.size >= 2 && (
        <div className="flex items-center gap-2 mt-2 flex-wrap" data-testid="face-mode-row">
          <div
            className="inline-flex rounded-full overflow-hidden text-[11px] font-medium"
            style={{ border: "1px solid rgba(255,255,255,0.18)" }}
          >
            <button
              type="button"
              onClick={() => onModeChange?.("and")}
              className="px-3 py-1.5"
              style={{
                background: faceMode !== "or" ? "rgba(127,75,255,0.35)" : "transparent",
                color: faceMode !== "or" ? "#fff" : "rgba(255,255,255,0.6)",
              }}
              data-testid="face-mode-and"
            >
              Together (all)
            </button>
            <button
              type="button"
              onClick={() => onModeChange?.("or")}
              className="px-3 py-1.5"
              style={{
                background: faceMode === "or" ? "rgba(34,226,255,0.30)" : "transparent",
                color: faceMode === "or" ? "#fff" : "rgba(255,255,255,0.6)",
              }}
              data-testid="face-mode-or"
            >
              Any of them
            </button>
          </div>
          <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.5)" }} data-testid="multi-face-hint">
            {faceMode === "or"
              ? "Photos with at least one selected person."
              : "Only photos with everyone selected — together in the same shot."}
          </span>
        </div>
      )}
    </div>
  );
}
